import React, { useState } from 'react';
import '../styles/SearchBar.css';
import { Mine } from '../pages/HomePage';

interface SearchBarProps {
  mines: Mine[]; // Array of mines to search through
  onResultSelect: (lat: number, lng: number) => void; // Callback when user selects a search result
  placeholder?: string; // Optional placeholder text for the input
}

// Minimum number of characters required to trigger search
const numberOfLetters = 2;

/**
 * SearchBar - Component for searching mines by title and content
 * Provides live search with dropdown results and loading states
 */
function SearchBar({ mines, onResultSelect, placeholder = "Vyhledat prvek..." }: SearchBarProps) {
  // Component state
  const [query, setQuery] = useState(''); // Current search query
  const [isOpen, setIsOpen] = useState(false); // Whether dropdown is visible
  const [isLoading, setIsLoading] = useState(false); // Loading state for search

  // Filter all mines based on search query (title and content)
  const allFilteredResults: Mine[] = query.length >= numberOfLetters 
    ? mines.filter((mine) => 
        mine.title.toLowerCase().includes(query.toLowerCase()) || 
        mine.content.toLowerCase().includes(query.toLowerCase())
      ) 
    : [];

  // Limit results to first 8 items for performance and UI
  const filteredResults: Mine[] = allFilteredResults.slice(0, 8);
  // Total number of matching results for display
  const numberAllFilteredResults: number = allFilteredResults.length;

  // Handle input changes and manage dropdown visibility
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    
    if (value.length >= numberOfLetters) {
      setIsOpen(true);
      setIsLoading(true);
      // Simulate loading delay for better UX
      setTimeout(() => setIsLoading(false), 300);
    } else {
      setIsOpen(false);
    }
  };

  // Clear search input and close dropdown
  const handleClear = () => {
    setIsOpen(false);
    setQuery('');
  };

  // Handle clicking on a search result
  const handleResultClick = (result: Mine) => {
    setQuery(result.title); // Set input to selected mine title
    onResultSelect(result.position[0], result.position[1]); // Notify parent component
  };
  
  return (
    <div className="search-container">
      {/* Search input with icon and clear button */}
      <div className="search-input-wrapper">
        {/* Search icon */}
        <div className="search-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
        </div>
        
        {/* Search input field */}
        <input
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onBlur={() => {
            // Clear search and close dropdown when input loses focus
            setTimeout(() => {
              setIsOpen(false);
            }, 200); // Small delay to allow click events on results to fire first
          }}
        />
        
        {/* Clear button - only shown when there's text */}
        {query && (
          <button className="search-clear" onClick={handleClear}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        )}
      </div>

      {/* Search results dropdown */}
      {isOpen && (
        <div className="search-dropdown">
          {isLoading ? (
            // Loading state
            <div className="search-loading">
              <div className="loading-spinner"></div>
              <span>Vyhledávám...</span>
            </div>
          ) : filteredResults.length > 0 ? (
            // Results found
            <>
              <div className="search-results">
                {filteredResults.map((result) => (
                  <div
                    key={result.id}
                    className="search-result"
                    onMouseDown={() => handleResultClick(result)}
                  >
                    <div className="result-content">
                      <div className="result-header">
                        <span className="result-title">{result.title}</span>
                      </div>
                      {/* Show content if available */}
                      {result.content && (
                        <p className="result-description">{result.content}</p>
                      )}
                      {/* Display coordinates */}
                      <span className="result-coordinates">
                        {Number(result.position[0]).toFixed(6)}, {Number(result.position[1]).toFixed(6)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Results counter */}
              <div className="search-footer">
                <span className="search-info">
                  Zobrazeno {filteredResults.length} výsledků z {numberAllFilteredResults}
                </span>
              </div>
            </>
          ) : query.length >= 2 ? (
            // No results found
            <div className="search-no-results">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <span>Žádné výsledky pro "{query}"</span>
              <p>Zkuste změnit hledaný výraz nebo použijte jiná klíčová slova</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
